/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {IconButton, IconButtonLabelPlacement} from '@ion/dls/src/components/buttons/IconButton';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {GridRefreshIcon} from '@ion/dls/src/icons/GridRefreshIcon';
import Render from '../../render/Render';
import {dlsColor} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {DLSTheme} from '../../types/types';
import {ButtonSpinner} from '../loading/ButtonSpinner';
import Tooltip from '../tooltip/Tooltip';

const useStyles = makeStyles((theme: DLSTheme) => ({
  spinner : {
    boxSizing       : 'border-box',
    padding         : '12px',
    backgroundColor : dlsColor('paleBlue'),
    borderRadius    : '50px',
    height          : '48px',
    width           : '48px',
    display         : 'flex',
    alignItems      : 'center',
    justifyContent  : 'center'
  }
}));

export const RefreshTooltip = function(props) {
  const {isPhone} = useMedia();
  return <Tooltip renderOnlyChildren={isPhone} placement='bottom' arrow {...props} />;
};

// defines the container props ...
export type RefreshGridButtonProps = {
  onRefresh: (params?: boolean) => void,
  disabled?: boolean,
  isLoading?: boolean,
  showCurrentLabel?: boolean,
  currentLabelPlacement?: IconButtonLabelPlacement,
  disableTooltip?: boolean
};

/**
 * The RefreshGridButton provides the Refresh Icon Button which provides the state
 * and labels common amongst refresh the grid
 *
 * ```typescript
 *   import {RefreshGridButton} from '@ion/dls/src/components/buttons/';
 *
 *   <RefreshGridButton onRefresh={onRefresh} />
 * ```
 */
export const RefreshGridButton = ({
  onRefresh,
  disabled,
  isLoading = false,
  showCurrentLabel = true,
  currentLabelPlacement = IconButtonLabelPlacement.STACKED,
  disableTooltip = false
}: RefreshGridButtonProps) => {
  const classes = useStyles();
  const {isCurrent: isCurrentTheme} = useThemeContext();
  const refreshLabel = translate('common:refresh');
  const button       = (
    <div>
      <Render>
        <Render.If condition={isLoading}>
          <div className={classes.spinner}>
            <ButtonSpinner size={18} margin={'7px 0px 1px 0px'} />
          </div>
        </Render.If>
        <Render.Else>
          <IconButton icon={<GridRefreshIcon />}
            onClick={onRefresh}
            label={isCurrentTheme ? refreshLabel : undefined}
            labelPlacement={currentLabelPlacement}
            hideLabel={isCurrentTheme && !showCurrentLabel}
            disabled={disabled || isLoading} />
        </Render.Else>
      </Render>
    </div>
  );

  if (isCurrentTheme || disableTooltip) {
    return button;
  }

  return (
    <RefreshTooltip title={refreshLabel}>
      {button}
    </RefreshTooltip>
  );
};

export default RefreshGridButton;
